import {AfterViewInit, Component, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import {AuthService} from '../../core/auth.service';
import {BaseService} from '../../shared/base.service';
import {SelfReportsModel} from './self-reports.model';
import {AppService} from '../../app.service';
import {DialogComponent} from '../../dialog/dialog.component';
import {ModalDirective} from 'ngx-bootstrap/modal';
import {TranslateService} from '@ngx-translate/core';
import {finalize} from 'rxjs/operators';
import {ServiceProviderSettingService} from '../../service-provider-setting/service-provider-setting.service';
import {SelfReportsService} from './self-reports.service';
import { BaseComponent } from '../../shared/base.component';

@Component({
    selector: 'self-reports-component',
    templateUrl: './self-reports.component.html',
    styleUrls: ['./self-reports.component.scss'],
    providers: [BaseService, SelfReportsService]
})

export class SelfReportsComponent extends BaseComponent implements OnInit, AfterViewInit {
    @ViewChild('dialogLogo', {static: false}) dialogLogo: DialogComponent;
    @ViewChild('modalDeleteSelfReport', {static: false}) modalDeleteSelfReport: ModalDirective;
    @ViewChild('overviewModal', {static: false}) overviewModal: ModalDirective;

    helpSelfReportProfile: any;
    readonly tableColumns = [
        ['id', 'ID', 'col-xs-1 id-header', 'customer_id', 'default'],
        ['name_', 'Self Report Name', 'col-xs-5', 'name', 'default'],
    ];

    @ViewChild('customModal', {static: false}) customModal: ModalDirective;
    @ViewChild('groupSelectionModal', {static: false}) groupSelectionModal: ModalDirective;
    items: any;
    totalItems = 0;
    deletingSelfReport: any;
    parentItem = 0;
    itemLanguage: string;
    domains: any;
    standards: any;
    feelings: any;
    groupsData: any;
    parentGroups: any;
    customSRStep = 0;
    totalSteps = 3;
    createForm : FormGroup;
    groupSelectionForm: FormGroup;
    circumstances: any;
    overviewData : any;

    constructor(public authService: AuthService,
                public router: Router,
                public baseService: BaseService,
                public appService: AppService,
                public route: ActivatedRoute,
                public translate: TranslateService,
                public fb: FormBuilder,
                public selfReportService: SelfReportsService) {
        super(authService, router, baseService, appService);
    }

    ngOnInit() {
        super.itemsPerPage = -1;
        super.ngOnInit();
        this.itemLanguage = 'de_CH';
        this.sortedColumn = 'name_' + this.itemLanguage;
        this.searchInfo = new SelfReportsModel();
        this.url = 'self-report-setting/self-report';
        this.token = this.authService.user['token'];
        this.languages = JSON.parse(localStorage.getItem('languages'));
    }

    ngAfterViewInit(): void {
        switch (this.params['action']) {
            case 'create': {
                this.openFormModal({action: 'create', component: 'sr', title: 'Add Self Report', url: this.url});
                break;
            }
            case 'update': {
                this.openFormModal({action: 'update', component: 'sr', id: this.params['id'], title: 'Edit Self Report', url: this.url});
                break;
            }
            case 'read': {
                this.openFormModal({
                    action: 'read', id: this.params['id'], title: 'Self Report Details',
                    content: this.params['content'], description: this.params['description'], url: this.url
                });
                break;
            }
            case 'activate':
            case 'delete' : {
                this.params['url'] = this.url;
                this.openConfirmModal(this.params);
                break;
            }
            default: {
                this.getItems();
                break;
            }
        }
    }

    openFormModal(params: Object) {
        this.initForm(params);
        let createForm = {};
        this.selfReportService.read(this.url, params['id'])
            .subscribe(response => {
                let selfReport = response['data'];
                this.domains = response['domains'];
                this.standards = response['types'];

                let disabled = params['action'] === 'read' ? true : false;

                for (let column in selfReport) {
                    if (selfReport.hasOwnProperty(column)) {
                        createForm[column] = {value: selfReport[column], disabled: disabled};
                    }
                }
                createForm['searchTxt'] = {value: null,  disabled: false};

                if (this.createForm && this.createForm !== null) {
                    this.createForm.reset(createForm);
                } else {
                    this.createForm = this.fb.group(createForm);
                    this.languages.forEach(language => {
                        this.createForm.controls['name_' + language['locale']].setValidators([<any>Validators.required, <any>Validators.minLength(3)]);
                    });
                }

                this.showModal();
            })
    }

    openConfigurationModal(params: Object) {
        this.initForm(params);
        this.selfReportService.readCustomSR(this.url, params['id'], params['step'] || this.customSRStep)
            .subscribe(response => {
                this.domains = response['domains'];
                this.circumstances = response['life_circumstanes'];
                this.feelings = response['items'] || [];
                this.groupsData = response['groups'];
                this.parentGroups = response['parent_groups'];
                this.totalSteps = response['total_step'];

                let form = {};
                let disabled = params['action'] === 'read' ? true : false;

                for (let column in response['data']) {
                    if (response['data'].hasOwnProperty(column)) {
                        form[column] = {value: response['data'][column], disabled: disabled};
                    }
                }
                form['searchTxt'] = {value: null, disabled: false};
                form['domains'] = {value: this.domains, disabled: false};
                form['items'] = {value: this.feelings, disabled: false};
                form['feelings'] = {value: this.feelings, disabled: false};
                form['sr_custom_id'] = {value: params['id'], disabled: false};
                form['show_circumstances'] = {value: response['circumstance_ids'].length > 0 ? true : false, disabled: false};
                form['circumstance_ids'] = {value: response['circumstance_ids'], disabled: false};
                form['groups'] = this.fb.array([]);

                this.form = this.fb.group(form);

                if (this.form != null) {
                    this.form.reset(form);
                }

                if (!this.groupsData.length) {
                    form['groups'].push(
                        this.fb.group({
                            id: new FormControl({value: '', disabled: false}, []),
                            name: new FormControl({value: '', disabled: false}, [Validators.required, <any>Validators.minLength(3)]),
                            trigger_no: new FormControl({value: '', disabled: false}, [<any>Validators.required, <any>Validators.min(1), Validators.pattern('^[0-9]*$')]),
                            feelings: new FormControl({value: [], disabled: false}, [Validators.required]),
                            min_age: new FormControl({value: 1, disabled: false}, []),
                            max_age: new FormControl({value: 999, disabled: false}, []),
                            parent_id: new FormControl({value: 0, disabled: false}, [])
                        })
                    );
                } else {
                    for (let index in this.groupsData) {
                        form['groups'].push(
                            this.fb.group({
                                id: new FormControl({value: this.groupsData[index]['id'], disabled: false}, []),
                                name: new FormControl({value: this.groupsData[index]['name'],disabled: false}, [Validators.required, <any>Validators.minLength(3)]),
                                trigger_no: new FormControl({value: this.groupsData[index]['trigger_no'],disabled: false}, [<any>Validators.required, <any>Validators.min(1),  Validators.pattern('^[0-9]*$')]),
                                feelings: new FormControl({value: this.groupsData[index]['feelings'],disabled: false}, [Validators.required]),
                                min_age: new FormControl({value: this.groupsData[index]['min_age'],disabled: false}, []),
                                max_age: new FormControl({value: this.groupsData[index]['max_age'],disabled: false}, []),
                                parent_id: new FormControl({value: this.groupsData[index]['parent_id'],disabled: false}, [])
                            })
                        );
                    }
                }

                this.showCustomModal();
            });
    }

    openGroupSelectionModal(params: Object) {
        this.initForm(params);
        this.selfReportService.getGroupSelection(this.url, params['id'])
            .subscribe(response => {
                this.domains = response['domains'];
                this.feelings = response['items'].length ? response['items'] : this.feelings;
                this.groupsData = response['groups'];

                let form = {};
                let disabled = params['action'] === 'read' ? true : false;

                form['searchTxt'] = {value: null, disabled: false};
                form['domains'] = {value: this.domains, disabled: false};
                form['items'] = {value: this.feelings, disabled: false};
                form['feelings'] = {value: this.feelings, disabled: false};
                form['sr_custom_id'] = {value: params['id'], disabled: false};
                form['groups'] = this.fb.array([]);

                this.groupSelectionForm = this.fb.group(form);

                if (this.groupSelectionForm != null) {
                    this.groupSelectionForm.reset(form);
                }

                if (!this.groupsData.length) {
                    let nameControls: Record<string, any> = {};
                    form['groups'].push(
                        this.fb.group({
                            id: new FormControl({value: '', disabled: disabled}, []),
                            name_en_GB: new FormControl({value: '', disabled: disabled}, [Validators.required, <any>Validators.minLength(3)]),
                            name_de_CH: new FormControl({value: '', disabled: disabled}, [Validators.required, <any>Validators.minLength(3)]),
                            name_fr_FR: new FormControl({value: '', disabled: disabled}, [Validators.required, <any>Validators.minLength(3)]),
                            name_it_IT: new FormControl({value: '', disabled: disabled}, [Validators.required, <any>Validators.minLength(3)]),
                            feelings: new FormControl({value: [], disabled: disabled}, [Validators.required]),
                        })
                    );
                } else {
                    for (let index in this.groupsData) {
                        let nameControls: Record<string, any> = {};
                        form['groups'].push(
                            this.fb.group({
                                id: new FormControl({value: this.groupsData[index]['id'], disabled: disabled}, []),
                                name_en_GB: new FormControl({value: this.groupsData[index]['name_en_GB'],disabled: disabled}, [Validators.required, <any>Validators.minLength(3)]),
                                name_de_CH: new FormControl({value: this.groupsData[index]['name_de_CH'],disabled: disabled}, [Validators.required, <any>Validators.minLength(3)]),
                                name_fr_FR: new FormControl({value: '', disabled: disabled}, [Validators.required, <any>Validators.minLength(3)]),
                                name_it_IT: new FormControl({value: '', disabled: disabled}, [Validators.required, <any>Validators.minLength(3)]),
                                feelings: new FormControl({value: this.groupsData[index]['feelings'],disabled: disabled}, [Validators.required]),
                            })
                        );
                    }
                }

                this.showGroupSelectionModal();
            });
    }

    changeStep($event) {
        this.hideModal();
        const params = {
            action: 'update',
            id: $event['id'],
            step: $event['step'] || 0,
            component: 'SrConfiguration',
            title: 'Config Self report',
            url: 'self-report/configuration'
        };
        this.customSRStep = $event['step'] || 0;
        return this.openConfigurationModal(params);
    }

    // display modal
    showCustomModal(): void {
        // prevent users from clicking outside to close the customModal since that does not set the url correctly
        this.customModal.config.backdrop = false;
        this.customModal.config.ignoreBackdropClick = true;
        this.customModal.config.keyboard = false;
        // this ensures the form is displayed only when the data is ready
        if (this.params['action'] !== 'delete' && this.params['action'] !== 'activate') {
            // if the form opened belongs to the component different from the component where it's opened
            this.formLoading = false;
        }
        // hide the app loading icon
        this.isLoading = false;
        this.isUpdated = false;
        this.resetErrors();
        this.customModal.show();
    }

    // user addition/update handler: send user info to DB
    submitCustomData(model: any, isValid: boolean, url?: string, isUpdated?: boolean): void {
        let data = {
            sr_custom_id: model['sr_custom_id'],
            step: this.customSRStep,
            circumstance_ids : model['show_circumstances'] ? model['circumstance_ids'] : [],
            groups: []
        };
        for (let index in model['groups']) {
            data['groups'].push({
                id: model['groups'][index]['id'] || 0,
                parent_id: model['groups'][index]['parent_id'] || 0,
                name: model['groups'][index]['name'],
                trigger_no: model['groups'][index]['trigger_no'],
                feelings: model['groups'][index]['feelings'],
                min_age: model['groups'][index]['min_age'],
                max_age: model['groups'][index]['max_age'],
            });
        }

        if (url == null) {
            url = this.url;
        }

        this.resetErrors();
        // this ensures validation messages will only be displayed when necessary
        this.formSubmitted = true;
        // only when the form is valid and its values have been changed
        if (isValid) {
            this.isLoading = true;

            this.selfReportService.updateCustomSR(url, data).pipe(
                finalize(() => {
                    this.isLoading = false;
                }))
                .subscribe(
                    response => {
                        this.handleResponse(response);
                        this.customSRStep++;
                        this.isUpdated = true;
                        // hide addition modal
                        this.hideModal();
                    },
                    error => {
                        this.isLoading = false;
                        this.errorMessage = this.appService.parseErrors(error);
                    }
                );
        }
        // return super.submit(data, this.form.valid, this.url, true);
    }

    // display modal
    showGroupSelectionModal(): void {
        // prevent users from clicking outside to close the customModal since that does not set the url correctly
        this.groupSelectionModal.config.backdrop = false;
        this.groupSelectionModal.config.ignoreBackdropClick = true;
        this.groupSelectionModal.config.keyboard = false;
        // this ensures the form is displayed only when the data is ready
        if (this.params['action'] !== 'delete' && this.params['action'] !== 'activate') {
            // if the form opened belongs to the component different from the component where it's opened
            this.formLoading = false;
        }
        // hide the app loading icon
        this.isLoading = false;
        this.isUpdated = false;
        this.resetErrors();
        this.groupSelectionModal.show();
    }

    // user addition/update handler: send user info to DB
    submitGroupSelection(model: any, isValid: boolean, url?: string, isUpdated?: boolean): void {
        let data = {
            sr_custom_id: model['sr_custom_id'],
            groups: []
        };
        for (let index in model['groups']) {
            data['groups'].push({
                id: model['groups'][index]['id'] || 0,
                name_en_GB: model['groups'][index]['name_en_GB'],
                name_de_CH: model['groups'][index]['name_de_CH'],
                feelings: model['groups'][index]['feelings'],
            });
        }

        if (url == null) {
            url = this.url;
        }

        this.resetErrors();
        // this ensures validation messages will only be displayed when necessary
        this.formSubmitted = true;
        // only when the form is valid and its values have been changed
        if (isValid) {
            this.isLoading = true;

            this.selfReportService.updateGroupSelection(url, data).pipe(
                finalize(() => {
                    this.isLoading = false;
                }))
                .subscribe(
                    response => {
                        this.handleResponse(response);
                        // hide addition modal
                        this.hideModal();
                    },
                    error => {
                        this.isLoading = false;
                        this.errorMessage = this.appService.parseErrors(error);
                    }
                );
        }
        // return super.submit(data, this.form.valid, this.url, true);
    }


    initForm(params: Object) {
        this.formSubmitted = false;
        super.initForm(params, false);
    }

    setData(data: any): void {
        this.items = data.items;
        this.totalItems = data.total || data.items.length;
        // if users are empty, hide the page numbers below the users list to prevent page redirection
        this.paginationHidden = this.items.length === 0;
        this.isLoading = false;
    }

    // user addition/update handler: send user info to DB
    submitCreateSR(model: any, isValid: boolean, url?: string, isUpdated?: boolean): void {

        return super.submit(this.createForm.value, this.createForm.valid, this.url, true);
    }

    deleteSelfReport(selfReport) {
        this.deletingSelfReport = selfReport;
        this.modalDeleteSelfReport.show();
    }

    submitDeleteSelfReport() {
        this.isLoading = true;
        this.selfReportService.deleteSelfReport(this.url, this.deletingSelfReport).subscribe(res => {

            if (res['success']) {
                this.modalDeleteSelfReport.hide();
                this.modal.hide();
                const message = res['message'];
                this.isUpdated = true;
                this.isLoading = false;
                this.getItems();

                if (message) {
                    const alert = this.dialog.openAlert({icon: 'far fa-trash-alt'});
                    alert.showAlert(message);
                }
            } else {
                const message = res['message'];
                this.isLoading = false;
                this.isUpdated = true;
                this.modalDeleteSelfReport.hide();
                if (message) {
                    const alert = this.dialog.openAlert({icon: 'fas fa-exclamation-triangle'});
                    alert.showAlert(message);
                }
            }
        });
    }

    hideModal() {
        this.modal.hide();
        this.customSRStep = 0;
        if(this.customModal){
            this.customModal.hide();
        }
        if(this.customModal){
            this.overviewModal.hide();
        }
        this.groupSelectionModal.hide();
    }

    openOverviewModal(params: Object) {
        this.initForm(params);
        this.selfReportService.readCustomSR(this.url, params['id'], -1)
            .subscribe(response => {
                this.overviewData = response['data'];
                this.isLoading = false;
                this.isUpdated = false;
                this.overviewModal.show();
            })
    }
}
